<template>
  <div v-if="projectLoaded">
    <div class="row project-header">
      <div class="col-md-6 position-relative">
        <h3>{{ project.name }}
          <a v-if="!showProjectImages && !showProjectPdfs" role="button" class="ms-2" @click="showImages()">
            <i class="material-icons">image</i>
          </a>
          <a v-if="!showProjectImages && !showProjectPdfs && userRole !== 'User'" role="button" class="ms-2" @click="showImageTypeModal = true">
            <i class="material-icons">photo_camera</i>
          </a>
          <a v-if="!showProjectImages && !showProjectPdfs" role="button" class="ms-2" @click="showPdfs()">
            <i class="material-icons">picture_as_pdf</i>
          </a>
          <a v-if="showProjectImages || showProjectPdfs" role="button" class="ms-2" @click="closeImages(); closeProjectPdfs()">
            <i class="material-icons">arrow_back</i>
          </a>
        </h3>
        <h5 class="text-muted">{{ project.key }}</h5>
        <h6 v-if="userRole !== 'User'" class="text-muted">{{ getStatusTranslation(project.projectStatusId) }}</h6>
        <h6 v-if="enableProjectPrices && userRole !== 'User'" class="text-muted">
          {{ currentProjectPrice }} Kr.
          <a role="button" class="ms-1" @click="handleUpdateProjectPrice()">
            <i class="material-icons">{{ enableManualProjectPriceSetting ? 'edit' : 'description' }}</i>
          </a>
        </h6>
        <div v-if="enableProjectPhases && projectPhases.length" class="project-phases">
          <span class="project-phases-label">
            <i class="material-icons">account_tree</i>{{ $t('projects.phases') }}
          </span>
          <span
            v-for="phase in projectPhases"
            :key="phase.id"
            class="project-phase-chip"
            :title="phase.description || ''"
          >{{ phase.name }}</span>
        </div>
      </div>
    </div>

    <!-- Price History Modal -->
    <modal v-model:show="showUpdatePriceModal">
      <card :title="$t('projects.priceHistory')" :on-cancel="closeUpdatePriceModal">
        <div class="row">
          <el-table :data="priceHistory">
            <el-table-column
              v-for="column in priceHistoryColumns"
              :key="column.label"
              :min-width="column.minWidth"
              :prop="column.prop"
              :label="column.label"
            />
          </el-table>
        </div>
        <div v-if="enableManualProjectPriceSetting" class="row mt-3">
          <div class="col-md-12">
            <material-input-field
              id="newPrice"
              v-model="newProjectPrice"
              :placeholder="$t('projects.newPrice')"
              :label="$t('projects.newPrice')"
              name="newPrice"
              variant="static"
            />
          </div>
        </div>
        <div v-if="enableManualProjectPriceSetting" class="row mt-3">
          <div class="col-sm d-flex justify-content-end">
            <material-button fill color="dark" type="standard" @click="updateProjectPrice">
              {{ $t('projects.updatePrice') }}
            </material-button>
          </div>
        </div>
      </card>
    </modal>

    <!-- Tables Section -->
    <div v-if="!showProjectImages && !showProjectPdfs" class="row">
      <div class="col-12">
        <card :title="cardTitle">
          <template v-if="enableExcelExport && userRole !== 'User'" #under-header>
            <div style="padding: 0 0 8px;">
              <el-button :icon="Download" size="small" @click="showExportDialog = true">{{ $t('dataExport.exportProjectReservations') }}</el-button>
            </div>
          </template>
          <template #table-select>
            <NavPills
              :model-value="activeIndex"
              :items="navPillItems"
              @update:model-value="initTable"
            />
          </template>

          <paginated-table
            v-if="currentTableConfig"
            :columns="currentTableConfig.columns"
            :data="currentTableConfig.data"
            :pagination="currentTableConfig.pagination"
            :ready="currentTableConfig.ready"
            :allow-click="currentTableConfig.allowClick"
            :enable-edit="false"
            :enable-delete="false"
            @row-click="currentTableConfig.onRowClick"
            @update-page="currentTableConfig.onUpdatePage"
          />
        </card>
      </div>
    </div>

    <!-- Image Gallery Section -->
    <div v-if="showProjectImages" class="row">
      <div class="col-12">
        <card :title="$t('projects.images') + ' - ' + (imageTabIndex === 0 ? $t('projects.before') : $t('projects.after'))" :on-add-object="userRole !== 'User' ? openImageUpload : null">
          <template #table-select>
            <NavPills
              :model-value="imageTabIndex"
              :items="imageNavPillItems"
              @update:model-value="initImageTab"
            />
          </template>

          <div class="thumbs">
            <img
              v-for="(item, index) in galleryItems"
              :key="index"
              :src="item.thumbnail"
              class="thumb"
              @click="activeImageIndex = index"
            />
          </div>

          <ImageLightbox
            v-model:index="activeImageIndex"
            :images="galleryItems"
            :deletable="userRole !== 'User'"
            @delete="deleteImage(projectImages[$event].id)"
          />
        </card>
      </div>
    </div>

    <!-- Image Type Choice Modal -->
    <modal v-model:show="showImageTypeModal" class="white-content">
      <h3 class="card-title">{{ $t('projects.selectImageType') }}</h3>
      <div class="row">
        <div class="col-sm">
          <material-button
            full-width
            color="success"
            @click="selectImageType('before')"
          >
            {{ $t('projects.before') }}
          </material-button>
        </div>
        <div class="col-sm">
          <material-button
            full-width
            color="danger"
            @click="selectImageType('after')"
          >
            {{ $t('projects.after') }}
          </material-button>
        </div>
      </div>
    </modal>

    <!-- Image Upload Modal -->
    <modal v-model:show="showImageUpload" class="overflow-auto">
      <card :title="$t('projects.addImage')">
        <div class="row mb-3">
          <div class="col-12">
            <file-pond
              ref="pond"
              name="images"
              :allow-multiple="true"
              :accepted-file-types="['image/jpeg', 'image/png', 'image/webp', 'image/heic']"
              :max-file-size="'8MB'"
              :label-idle="$t('projects.pressHere')"
              @updatefiles="handleImageUpdate"
            />
          </div>
        </div>
        <div class="row">
          <div class="col-sm d-flex justify-content-end">
            <material-button color="dark" type="standard" fill :disabled="imageUploadInProgress" @click="uploadImagesToS3">
              <i v-if="imageUploadInProgress" class="fas fa-spinner fa-spin me-2"></i>
              <span v-if="uploadStage === 'compressing'">{{ $t('common.compressing', compressionProgress) }}</span>
              <span v-else-if="uploadStage === 'uploading'">{{ $t('common.uploading') }}</span>
              <span v-else>{{ $t('common.confirm') }}</span>
            </material-button>
          </div>
        </div>
      </card>
    </modal>

    <!-- PDF List Section -->
    <div v-if="showProjectPdfs" class="row">
      <div class="col-12">
        <card :title="$t('projects.pdfs')" :on-add-object="userRole !== 'User' ? openPdfUpload : null">
          <div v-if="projectPdfsList.length === 0" class="text-muted">{{ $t('projects.noPdfs') }}</div>
          <div v-for="pdf in projectPdfsList" :key="pdf.id" class="pdf-item d-flex align-items-center mb-2">
            <i class="material-icons me-2 text-danger">picture_as_pdf</i>
            <span class="me-3">{{ pdf.filename }}</span>
            <a role="button" class="me-2" :title="$t('projects.viewPdf')" @click="viewPdf(pdf)">
              <i class="material-icons">visibility</i>
            </a>
            <a role="button" class="me-2" :title="$t('projects.downloadPdf')" @click="downloadPdf(pdf)">
              <i class="material-icons">download</i>
            </a>
            <a v-if="userRole !== 'User'" role="button" :title="$t('common.delete')" @click="deletePdf(pdf.id)">
              <i class="material-icons text-danger">delete</i>
            </a>
          </div>
        </card>
      </div>
    </div>

    <!-- PDF Upload Modal -->
    <modal v-model:show="showPdfUpload" class="overflow-auto">
      <card :title="$t('projects.addPdf')">
        <div class="row mb-3">
          <div class="col-12">
            <input ref="pdfFileInput" type="file" multiple accept="application/pdf" @change="handlePdfFiles" />
          </div>
        </div>
        <div class="row">
          <div class="col-sm d-flex justify-content-end">
            <material-button color="dark" type="standard" fill :disabled="pdfUploadInProgress" @click="uploadPdfsToS3">
              <i v-if="pdfUploadInProgress" class="fas fa-spinner fa-spin me-2"></i>
              {{ $t('common.confirm') }}
            </material-button>
          </div>
        </div>
      </card>
    </modal>

    <data-export-modal
      v-model:show="showExportDialog"
      type="projectReservations"
      :project-id="projectId"
    />
  </div>
</template>

<script>
import { jwtDecode } from "jwt-decode";
import Cookies from 'js-cookie';
import api from "../../services/api";
import { getApiErrorMessage } from "../../utils/apiError";
import axios from 'axios';
import { compressImagesForUpload, uploadToPresignedUrl } from '../../services/imageUpload';
import { ElTable, ElTableColumn, ElButton } from 'element-plus';
import { Download } from '@element-plus/icons-vue';
import { shallowRef } from 'vue';
import { getCustomerConfig } from '../Settings/customerConfigHelper';
import Card from "../../components/Card.vue";
import PaginatedTable from "../../components/PaginatedTable.vue";
import Modal from "../../components/Modals/Modal.vue";
import MaterialButton from "../../components/MaterialButton.vue";
import MaterialInputField from "../../components/MaterialInputField.vue";
import NavPills from "../../components/NavPills.vue";
import DataExportModal from "../../components/DataExportModal.vue";
import ImageLightbox from "../../components/ImageLightbox.vue";
import vueFilePond from 'vue-filepond';
import 'filepond/dist/filepond.min.css';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
const FilePond = vueFilePond(FilePondPluginFileValidateType, FilePondPluginImagePreview);

export default {
  components: {
    ElTable,
    ElTableColumn,
    ElButton,
    Card,
    PaginatedTable,
    Modal,
    MaterialButton,
    MaterialInputField,
    NavPills,
    DataExportModal,
    ImageLightbox,
    FilePond,
  },
  data() {
    return {
      projectId: '',
      project: {},
      projectLoaded: false,
      userRole: '',
      activeIndex: 0,
      cardTitle: this.$t('projects.toolsAssignedToProject'),
      Download: shallowRef(Download),
      showExportDialog: false,
      enableExcelExport: false,

      // Pagination per table
      currentlyReservedPagination: { perPage: 10, currentPage: 1, total: 0 },
      historyPagination: { perPage: 10, currentPage: 1, total: 0 },
      loanRentPagination: { perPage: 10, currentPage: 1, total: 0 },
      loanRentHistoryPagination: { perPage: 10, currentPage: 1, total: 0 },
      projectAssignmentsPagination: { perPage: 10, currentPage: 1, total: 0 },

      // Table data
      currentlyReservedTable: {
        columns: [
          { prop: 'image', label: this.$t('common.image'), width: 80, mobileWidth: 100, mobile: true, isImage: true },
          { prop: 'tool', label: this.$t('common.tool'), minWidth: 200 },
          { prop: 'quantity', label: this.$t('tools.quantity'), minWidth: 60 },
          { prop: 'daysLeft', label: this.$t('tools.daysLeft'), minWidth: 60 },
          { prop: 'reserved', label: this.$t('tools.reserved'), minWidth: 100 },
          { prop: 'reservedBy', label: this.$t('tools.reservedBy'), minWidth: 200 },
          { label: this.$t('common.info'), minWidth: 150, mobile: true, isCompound: true, compound: { 'common.tool': 'tool', 'tools.quantity': 'quantity', 'tools.daysLeft': 'daysLeft', 'tools.reserved': 'reserved', 'tools.reservedBy': 'reservedBy' } },
        ],
        data: [],
      },
      historyTable: {
        columns: [
          { prop: 'image', label: this.$t('common.image'), width: 80, mobileWidth: 100, mobile: true, isImage: true },
          { prop: 'tool', label: this.$t('common.tool'), minWidth: 200 },
          { prop: 'quantity', label: this.$t('tools.quantity'), minWidth: 60 },
          { prop: 'reserved', label: this.$t('tools.reserved'), minWidth: 100 },
          { prop: 'returned', label: this.$t('tools.returned'), minWidth: 100 },
          { prop: 'reservedBy', label: this.$t('tools.reservedBy'), minWidth: 200 },
          { label: this.$t('common.info'), minWidth: 150, mobile: true, isCompound: true, compound: { 'common.tool': 'tool', 'tools.quantity': 'quantity', 'tools.reserved': 'reserved', 'tools.returned': 'returned', 'tools.reservedBy': 'reservedBy' } },
        ],
        data: [],
      },
      loanRentTable: {
        columns: [
          { prop: 'image', label: this.$t('common.image'), width: 80, mobileWidth: 100, mobile: true, isImage: true },
          { prop: 'tool', label: this.$t('common.tool'), minWidth: 150 },
          { prop: 'quantity', label: this.$t('tools.quantity'), minWidth: 60 },
          { prop: 'loanedRented', label: this.$t('tools.loanedRented'), minWidth: 60 },
          { prop: 'externalUser', label: this.$t('tools.loanedRentedTo'), minWidth: 100 },
          { prop: 'reserved', label: this.$t('tools.loanedRentedDate'), minWidth: 80 },
          { prop: 'loanedRentedBy', label: this.$t('tools.loanedRentedBy'), minWidth: 170 },
          { label: this.$t('common.info'), minWidth: 150, mobile: true, isCompound: true, compound: { 'common.tool': 'tool', 'tools.quantity': 'quantity', 'tools.loanedRented': 'loanedRented', 'tools.loanedRentedTo': 'externalUser', 'tools.loanedRentedDate': 'reserved', 'tools.loanedRentedBy': 'loanedRentedBy' } },
        ],
        data: [],
      },
      loanRentHistoryTable: {
        columns: [
          { prop: 'tool', label: this.$t('common.tool'), minWidth: 150 },
          { prop: 'quantity', label: this.$t('tools.quantity'), minWidth: 60 },
          { prop: 'externalReservationType', label: this.$t('toolGroups.reservationType'), minWidth: 200 },
          { prop: 'reserved', label: this.$t('tools.reserved'), minWidth: 80 },
          { prop: 'returned', label: this.$t('tools.returned'), minWidth: 80 },
          { prop: 'loanedRentedBy', label: this.$t('tools.loanedRentedBy'), minWidth: 200 },
          { label: this.$t('common.info'), minWidth: 150, mobile: true, isCompound: true, compound: { 'common.tool': 'tool', 'tools.quantity': 'quantity', 'toolGroups.reservationType': 'externalReservationType', 'tools.reserved': 'reserved', 'tools.returned': 'returned', 'tools.loanedRentedBy': 'loanedRentedBy' } },
        ],
        data: [],
      },
      projectAssignmentsTable: {
        columns: [
          { prop: 'user', label: this.$t('common.user'), minWidth: 200 },
          { prop: 'date', label: this.$t('common.date'), minWidth: 100 },
          { label: this.$t('common.info'), minWidth: 150, mobile: true, isCompound: true, compound: { 'common.user': 'user', 'common.date': 'date' } },
        ],
        data: [],
      },

      // Ready flags
      currentlyReservedReady: false,
      historyReady: false,
      loanRentReady: false,
      loanRentHistoryReady: false,
      projectAssignmentsReady: false,

      // Price
      currentProjectPrice: 0,
      enableProjectPrices: false,
      enableManualProjectPriceSetting: false,
      showUpdatePriceModal: false,
      newProjectPrice: '',
      priceHistory: [],
      priceHistoryColumns: [
        { prop: 'date', label: this.$t('common.date'), minWidth: 120 },
        { prop: 'price', label: this.$t('projects.price'), minWidth: 200 },
      ],

      // Images
      showProjectImages: false,
      showImageUpload: false,
      showImageTypeModal: false,
      imageUploadInProgress: false,
      uploadStage: null,
      compressionProgress: { done: 0, total: 0 },
      imageTabIndex: 0,
      selectedImageType: null,
      projectImages: [],
      galleryItems: [],
      activeImageIndex: null,
      uploadImages: [],

      // PDFs
      showProjectPdfs: false,
      showPdfUpload: false,
      pdfUploadInProgress: false,
      projectPdfsList: [],
      pdfUploadFiles: [],

      // Config flags
      loanRentCustomer: false,
      enablePreCreatedProjectAssignments: false,
      enableDayProjectAssignments: false,
      enableProjectAssignmentTypes: false,
      enableProjectPhases: false,
      projectPhases: [],
    };
  },
  computed: {
    tabCategories() {
      const list = this.userRole !== 'User'
        ? [
          { name: this.$t('projects.toolsAssignedToProject'), icon: 'settings' },
          { name: this.$t('projects.reservationHistory'), icon: 'history' },
          { name: this.$t('projects.toolsLoanedRentedOnProject'), icon: 'swap_horiz' },
          { name: this.$t('projects.loanRentHistory'), icon: 'receipt_long' },
        ]
        : [{ name: this.$t('projects.toolsAssignedToProject'), icon: 'settings' }];

      if (this.userRole !== 'User' && (this.enablePreCreatedProjectAssignments || this.enableDayProjectAssignments)) {
        list.push({ name: this.$t('projectAssignments.assignmentsWorked'), icon: 'assignment' });
      }
      return list;
    },
    navPillItems() {
      return this.tabCategories.map(c => ({ label: c.name, icon: c.icon }));
    },
    imageNavPillItems() {
      return [
        { label: this.$t('projects.before'), icon: 'photo_camera_back' },
        { label: this.$t('projects.after'), icon: 'photo_camera_front' },
      ];
    },

    tableConfigs() {
      const configs = [
        {
          columns: this.currentlyReservedTable.columns,
          data: this.currentlyReservedTable.data,
          pagination: this.currentlyReservedPagination,
          ready: this.currentlyReservedReady,
          allowClick: true,
          onRowClick: this.goToTool,
          onUpdatePage: (page) => { this.currentlyReservedPagination.currentPage = page; this.fetchCurrentlyReserved(); },
        },
      ];

      if (this.userRole !== 'User') {
        configs.push({
          columns: this.historyTable.columns,
          data: this.historyTable.data,
          pagination: this.historyPagination,
          ready: this.historyReady,
          allowClick: true,
          onRowClick: this.goToTool,
          onUpdatePage: (page) => { this.historyPagination.currentPage = page; this.fetchHistory(); },
        });

        configs.push({
          columns: this.loanRentTable.columns,
          data: this.loanRentTable.data,
          pagination: this.loanRentPagination,
          ready: this.loanRentReady,
          allowClick: true,
          onRowClick: this.goToTool,
          onUpdatePage: (page) => { this.loanRentPagination.currentPage = page; this.fetchCurrentlyLoanedRented(); },
        });

        configs.push({
          columns: this.loanRentHistoryTable.columns,
          data: this.loanRentHistoryTable.data,
          pagination: this.loanRentHistoryPagination,
          ready: this.loanRentHistoryReady,
          allowClick: true,
          onRowClick: this.goToTool,
          onUpdatePage: (page) => { this.loanRentHistoryPagination.currentPage = page; this.fetchLoanRentHistory(); },
        });

        if (this.enablePreCreatedProjectAssignments || this.enableDayProjectAssignments) {
          configs.push({
            columns: this.projectAssignmentsTable.columns,
            data: this.projectAssignmentsTable.data,
            pagination: this.projectAssignmentsPagination,
            ready: this.projectAssignmentsReady,
            allowClick: false,
            onRowClick: () => {},
            onUpdatePage: (page) => { this.projectAssignmentsPagination.currentPage = page; this.fetchProjectAssignments(); },
          });
        }
      }

      return configs;
    },

    currentTableConfig() {
      return this.tableConfigs[this.activeIndex];
    },
  },
  watch: {
    showImageUpload(newValue) {
      if (!newValue) this.clearFilePond();
    },
  },
  created() {
    this.projectId = this.$route.params.projectId;
    const token = Cookies.get('accessToken');
    const decodedToken = jwtDecode(token);
    this.userRole = decodedToken.role;

    this.fetchProjectData();

    getCustomerConfig('EnableProjectAssignmentTypes').then(config => {
      this.enableProjectAssignmentTypes = config === '' ? false : config;
      if (this.enableProjectAssignmentTypes) {
        this.projectAssignmentsTable.columns = [
          { prop: 'user', label: this.$t('common.user'), minWidth: 200 },
          { prop: 'type', label: this.$t('projectAssignments.type'), minWidth: 200 },
          { prop: 'date', label: this.$t('common.date'), minWidth: 100 },
          { label: this.$t('common.info'), minWidth: 150, mobile: true, isCompound: true, compound: { 'common.user': 'user', 'projectAssignments.type': 'type', 'common.date': 'date' } },
        ];
      }
    });
    getCustomerConfig('EnableDayProjectAssignments').then(config => {
      this.enableDayProjectAssignments = config === '' ? false : config;
    });
    getCustomerConfig('EnablePreCreatedProjectAssignments').then(config => {
      this.enablePreCreatedProjectAssignments = config === '' ? false : config;
    });
    getCustomerConfig('EnableManualProjectPriceSetting').then(config => {
      this.enableManualProjectPriceSetting = config === '' ? false : config;
    });
    getCustomerConfig('EnableProjectPrices').then(config => {
      this.enableProjectPrices = config === '' ? false : config;
    });
    getCustomerConfig('EnableExcelExport').then(config => {
      this.enableExcelExport = config === '' ? false : config;
    });
    getCustomerConfig('EnableProjectPhases').then(config => {
      this.enableProjectPhases = config === true;
      if (this.enableProjectPhases) {
        this.fetchProjectPhases();
        this.addPhaseColumn(this.currentlyReservedTable.columns);
        this.addPhaseColumn(this.historyTable.columns);
        this.addPhaseColumn(this.loanRentTable.columns);
        this.addPhaseColumn(this.loanRentHistoryTable.columns);
      }
    });
    getCustomerConfig('DefaultPageSize').then(config => {
      const pageSize = config === '' ? 10 : config;
      this.currentlyReservedPagination.perPage = pageSize;
      this.historyPagination.perPage = pageSize;
      this.loanRentPagination.perPage = pageSize;
      this.loanRentHistoryPagination.perPage = pageSize;
      this.projectAssignmentsPagination.perPage = pageSize;

      getCustomerConfig('LoanRentCustomer').then(lrConfig => {
        this.loanRentCustomer = lrConfig === '' ? false : lrConfig;
        if (!this.loanRentCustomer || this.userRole === 'User') {
          this.fetchCurrentlyReserved();
        } else {
          this.initTable(2);
        }
      });
    });
  },
  mounted() {},
  methods: {
    initTable(index) {
      this.activeIndex = index;
      this.resetReadyFlags();
      this.fetchActiveTable();
    },

    resetReadyFlags() {
      this.currentlyReservedReady = false;
      this.historyReady = false;
      this.loanRentReady = false;
      this.loanRentHistoryReady = false;
      this.projectAssignmentsReady = false;
    },

    fetchActiveTable() {
      if (this.userRole !== 'User') {
        switch (this.activeIndex) {
          case 0:
            this.cardTitle = this.$t('projects.toolsAssignedToProject');
            this.fetchCurrentlyReserved();
            break;
          case 1:
            this.cardTitle = this.$t('projects.reservationHistory');
            this.fetchHistory();
            break;
          case 2:
            this.cardTitle = this.$t('projects.toolsLoanedRentedOnProject');
            this.fetchCurrentlyLoanedRented();
            break;
          case 3:
            this.cardTitle = this.$t('projects.loanRentHistory');
            this.fetchLoanRentHistory();
            break;
          default:
            this.cardTitle = this.$t('projectAssignments.assignmentsWorked');
            this.fetchProjectAssignments();
            break;
        }
      } else {
        this.cardTitle = this.$t('projects.toolsAssignedToProject');
        this.fetchCurrentlyReserved();
      }
    },

    async fetchProjectData() {
      try {
        const response = await api.get(`/Project/${this.projectId}`);
        this.project = response.data;
        if (this.project.projectPrices && this.project.projectPrices.length > 0) {
          this.priceHistory = this.project.projectPrices.map(item => ({
            date: this.formatDate(item.date),
            price: item.price + ' Kr.',
          }));
          const activePrice = this.project.projectPrices.find(x => x.active);
          if (activePrice) this.currentProjectPrice = activePrice.price;
        }
        this.projectLoaded = true;
      } catch (error) {
        console.error('API request error:', error); // eslint-disable-line no-console
        this.$toast.danger(this.$t('common.unableToRetrieveData'), { timeout: 0 });
      }
    },

    // Inserts a phase column before the mobile compound column when phases are enabled.
    addPhaseColumn(columns) {
      const compoundIndex = columns.findIndex(c => c.isCompound);
      if (compoundIndex !== -1) columns[compoundIndex].compound['projects.phase'] = 'phase';
      columns.splice(compoundIndex === -1 ? columns.length : compoundIndex, 0, { prop: 'phase', label: this.$t('projects.phase'), minWidth: 110 });
    },

    async fetchProjectPhases() {
      try {
        const response = await api.get(`/Project/${this.projectId}/Phases`);
        this.projectPhases = response.data;
      } catch (error) {
        console.error('API request error:', error); // eslint-disable-line no-console
      }
    },

    async fetchCurrentlyReserved() {
      const url = `/Tool/Reservations?currentPage=${this.currentlyReservedPagination.currentPage}&pageSize=${this.currentlyReservedPagination.perPage}&projectId=${this.projectId}`;
      try {
        const response = await api.get(url);
        this.currentlyReservedPagination.total = response.data.totalItems;
        this.currentlyReservedTable.data = response.data.result.map(item => {
          let daysLeft = '';
          if (item.tool.maxReservationDays && item.tool.maxReservationDays !== 0) {
            const daysSince = Math.round((new Date() - new Date(item.reservedTimestamp)) / (24 * 60 * 60 * 1000));
            daysLeft = item.tool.maxReservationDays - daysSince;
          } else {
            daysLeft = '\u221E';
          }
          return {
            toolId: item.toolId,
            image: 'data:image/jpeg;base64,' + item.tool.toolImageThumbnailBytes,
            tool: `#${item.tool.customerToolId} ${item.tool.name}`,
            quantity: item.quantity ?? 1,
            reserved: new Date(item.reservedTimestamp).toLocaleDateString('is-IS'),
            daysLeft,
            reservedBy: item.user.firstName + ' ' + item.user.lastName,
            phase: item.projectPhaseName || '',
          };
        });
        this.currentlyReservedReady = true;
      } catch (error) {
        console.error(error); // eslint-disable-line no-console
        this.$toast.danger(this.$t('common.unableToRetrieveData'), { timeout: 0 });
      }
    },

    async fetchHistory() {
      if (this.userRole === 'User') return;
      const url = `/Tool/Reservations?currentPage=${this.historyPagination.currentPage}&pageSize=${this.historyPagination.perPage}&projectId=${this.projectId}&unreserved=true`;
      try {
        const response = await api.get(url);
        this.historyPagination.total = response.data.totalItems;
        this.historyTable.data = response.data.result.map(item => ({
          toolId: item.toolId,
          image: 'data:image/jpeg;base64,' + item.tool.toolImageThumbnailBytes,
          tool: `#${item.tool.customerToolId} ${item.tool.name}`,
          quantity: item.quantity ?? 1,
          reserved: new Date(item.reservedTimestamp).toLocaleDateString('is-IS'),
          returned: new Date(item.unreservedTimestamp).toLocaleDateString('is-IS'),
          reservedBy: item.user.firstName + ' ' + item.user.lastName,
          phase: item.projectPhaseName || '',
        }));
        this.historyReady = true;
      } catch (error) {
        console.error(error); // eslint-disable-line no-console
        this.$toast.danger(this.$t('common.unableToRetrieveData'), { timeout: 0 });
      }
    },

    async fetchCurrentlyLoanedRented() {
      if (this.userRole === 'User') return;
      const url = `/Tool/Reservations/External?currentPage=${this.loanRentPagination.currentPage}&pageSize=${this.loanRentPagination.perPage}&projectId=${this.projectId}`;
      try {
        const response = await api.get(url);
        this.loanRentPagination.total = response.data.totalItems;
        this.loanRentTable.data = response.data.result.map(item => ({
          toolId: item.toolId,
          image: 'data:image/jpeg;base64,' + item.tool.toolImageThumbnailBytes,
          tool: `#${item.tool.customerToolId} ${item.tool.name}`,
          quantity: item.quantity ?? 1,
          reserved: new Date(item.reservedTimestamp).toLocaleDateString('is-IS'),
          loanedRented: item.rented ? this.$t('tools.rented') : this.$t('tools.loaned'),
          externalUser: item.externalUser,
          loanedRentedBy: item.user.firstName + ' ' + item.user.lastName,
          phase: item.projectPhaseName || '',
        }));
        this.loanRentReady = true;
      } catch (error) {
        console.error(error); // eslint-disable-line no-console
        this.$toast.danger(this.$t('common.unableToRetrieveData'), { timeout: 0 });
      }
    },

    async fetchLoanRentHistory() {
      if (this.userRole === 'User') return;
      const url = `/Tool/Reservations/External?currentPage=${this.loanRentHistoryPagination.currentPage}&pageSize=${this.loanRentHistoryPagination.perPage}&projectId=${this.projectId}&unreserved=true&excludeImages=true`;
      try {
        const response = await api.get(url);
        this.loanRentHistoryPagination.total = response.data.totalItems;
        this.loanRentHistoryTable.data = response.data.result.map(item => {
          const type = item.rented
            ? this.$t('tools.rented') + ' - ' + item.externalUser
            : this.$t('tools.loaned') + ' - ' + item.externalUser;
          return {
            toolId: item.toolId,
            tool: `#${item.tool.customerToolId} ${item.tool.name}`,
            quantity: item.quantity ?? 1,
            externalReservationType: type,
            reserved: new Date(item.reservedTimestamp).toLocaleDateString('is-IS'),
            returned: new Date(item.unreservedTimestamp).toLocaleDateString('is-IS'),
            loanedRentedBy: item.user.firstName + ' ' + item.user.lastName,
            phase: item.projectPhaseName || '',
          };
        });
        this.loanRentHistoryReady = true;
      } catch (error) {
        console.error(error); // eslint-disable-line no-console
        this.$toast.danger(this.$t('common.unableToRetrieveData'), { timeout: 0 });
      }
    },

    async fetchProjectAssignments() {
      if (this.userRole === 'User') return;
      const url = `/Project/Assignments/Paginated?projectId=${this.projectId}&currentPage=${this.projectAssignmentsPagination.currentPage}&pageSize=${this.projectAssignmentsPagination.perPage}`;
      try {
        const response = await api.get(url);
        this.projectAssignmentsPagination.total = response.data.totalItems;
        this.projectAssignmentsTable.data = response.data.result.map(item => ({
          user: item.user ? `${item.user.firstName} ${item.user.lastName}` : 'N/A',
          date: new Date(item.startTime).toLocaleDateString('is-IS'),
          type: this.enableProjectAssignmentTypes && item.projectAssignmentType ? item.projectAssignmentType.type : 'N/A',
        }));
        this.projectAssignmentsReady = true;
      } catch (error) {
        console.error(error); // eslint-disable-line no-console
        this.$toast.danger(this.$t('common.unableToRetrieveData'), { timeout: 0 });
      }
    },

    // Price methods
    handleUpdateProjectPrice() {
      this.showUpdatePriceModal = true;
    },

    closeUpdatePriceModal() {
      this.showUpdatePriceModal = false;
      this.newProjectPrice = '';
    },

    async updateProjectPrice() {
      if (isNaN(this.newProjectPrice)) {
        this.$toast.warning(this.$t('errors.priceMustBeNumber'), { timeout: 2500 });
        return;
      }
      try {
        const response = await api.post('/Project/Price', {
          projectId: this.projectId,
          price: Number(this.newProjectPrice),
        });
        this.currentProjectPrice = response.data.price;
        this.priceHistory.unshift({ date: this.formatDate(response.data.date), price: response.data.price + ' Kr.' });
        this.closeUpdatePriceModal();
      } catch (error) {
        console.error('API request error:', error); // eslint-disable-line no-console
        this.$toast.danger(this.$t('common.unableToRetrieveData'), { timeout: 0 });
      }
    },

    // Image methods
    showImages() {
      const isBefore = this.imageTabIndex === 0;
      const params = isBefore ? 'before=true' : 'after=true';
      api.get(`/Project/${this.projectId}/Images?${params}`)
        .then(response => {
          this.projectImages = response.data;
          this.galleryItems = this.projectImages.map(img => ({
            src: img.downloadUrl,
            thumbnail: img.downloadUrl,
          }));
        })
        .catch(error => {
          console.error('API request error:', error); // eslint-disable-line no-console
          this.$toast.danger(this.$t('common.unableToRetrieveData'), { timeout: 0 });
        });
      this.showProjectImages = true;
      this.showImageUpload = false;
    },

    initImageTab(index) {
      this.imageTabIndex = index;
      this.activeImageIndex = null;
      this.showImages();
    },

    closeImages() {
      this.showProjectImages = false;
    },

    openImageUpload() {
      this.selectedImageType = this.imageTabIndex === 0 ? 'before' : 'after';
      this.showImageUpload = true;
    },

    selectImageType(type) {
      this.showImageTypeModal = false;
      this.selectedImageType = type;
      this.imageTabIndex = type === 'after' ? 1 : 0;
      this.showProjectImages = true;
      this.showImages();
      this.showImageUpload = true;
    },

    handleImageUpdate(items) {
      this.uploadImages = items;
    },

    clearFilePond() {
      if (this.$refs.pond) this.$refs.pond.removeFiles();
      this.uploadImages = [];
    },

    async uploadImagesToS3() {
      if (this.imageUploadInProgress) return;
      this.imageUploadInProgress = true;
      try {
        if (!this.uploadImages.length) {
          this.$toast.danger(this.$t('projects.noImagesSelected'), { timeout: 0 });
          this.imageUploadInProgress = false;
          return;
        }
        const rawFiles = this.uploadImages.map(item => item.file);
        this.uploadStage = 'compressing';
        this.compressionProgress = { done: 0, total: rawFiles.length };
        const prepared = await compressImagesForUpload(rawFiles, undefined, (done, total) => {
          this.compressionProgress = { done, total };
        });
        this.uploadStage = 'uploading';
        const imageRequests = prepared.map(p => ({
          projectId: this.projectId,
          filename: p.filename,
          before: this.selectedImageType === 'before' ? true : null,
          after: this.selectedImageType === 'after' ? true : null,
        }));
        const response = await api.post('/Project/Images/', { images: imageRequests });
        const uploadInfos = response.data;

        await Promise.all(uploadInfos.map((info, i) =>
          uploadToPresignedUrl(info.uploadUrl, prepared[i].blob, prepared[i].contentType)
        ));
        this.uploadStage = null;

        this.$toast.success(this.$t('projects.imageUploaded'), { timeout: 2500 });
        this.imageUploadInProgress = false;
        this.clearFilePond();
        this.imageTabIndex = this.selectedImageType === 'after' ? 1 : 0;
        this.showImageUpload = false;
        this.showImages();
      } catch (error) {
        console.error('API request error:', error); // eslint-disable-line no-console
        this.$toast.danger(getApiErrorMessage(this.$t, error), { timeout: 0 });
        this.imageUploadInProgress = false;
        this.uploadStage = null;
      }
    },

    deleteImage(id) {
      api.delete(`/Project/Image/${id}`)
        .then(() => {
          this.$toast.success(this.$t('delete.deleted'), { timeout: 2500 });
          this.activeImageIndex = null;
          this.showImages();
        })
        .catch(error => {
          console.error('API request error:', error); // eslint-disable-line no-console
        });
    },

    // PDF methods
    showPdfs() {
      api.get(`/Project/${this.projectId}/Pdfs`)
        .then(response => {
          this.projectPdfsList = response.data;
        })
        .catch(error => {
          console.error('API request error:', error); // eslint-disable-line no-console
          this.$toast.danger(this.$t('common.unableToRetrieveData'), { timeout: 0 });
        });
      this.showProjectPdfs = true;
      this.showPdfUpload = false;
    },

    closeProjectPdfs() {
      this.showProjectPdfs = false;
    },

    openPdfUpload() {
      this.showPdfUpload = true;
    },

    handlePdfFiles(event) {
      this.pdfUploadFiles = Array.from(event.target.files);
    },

    async uploadPdfsToS3() {
      if (this.pdfUploadInProgress) return;
      this.pdfUploadInProgress = true;
      try {
        if (!this.pdfUploadFiles.length) {
          this.$toast.danger(this.$t('projects.noPdfsSelected'), { timeout: 0 });
          this.pdfUploadInProgress = false;
          return;
        }
        const pdfRequests = this.pdfUploadFiles.map(file => ({
          projectId: this.projectId,
          filename: file.name,
        }));
        const response = await api.post('/Project/Pdfs/', { pdfs: pdfRequests });
        const uploadInfos = response.data;

        await Promise.all(uploadInfos.map((info, i) =>
          axios.put(info.uploadUrl, this.pdfUploadFiles[i], {
            headers: { 'Content-Type': 'application/pdf' },
          })
        ));

        this.$toast.success(this.$t('projects.pdfUploaded'), { timeout: 2500 });
        this.pdfUploadInProgress = false;
        this.pdfUploadFiles = [];
        if (this.$refs.pdfFileInput) this.$refs.pdfFileInput.value = '';
        this.showPdfUpload = false;
        this.showPdfs();
      } catch (error) {
        console.error('API request error:', error); // eslint-disable-line no-console
        this.$toast.danger(getApiErrorMessage(this.$t, error), { timeout: 0 });
        this.pdfUploadInProgress = false;
      }
    },

    viewPdf(pdf) {
      window.open(pdf.downloadUrl, '_blank');
    },

    downloadPdf(pdf) {
      const link = document.createElement('a');
      link.href = pdf.downloadUrl;
      link.download = pdf.filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    deletePdf(id) {
      api.delete(`/Project/Pdf/${id}`)
        .then(() => {
          this.$toast.success(this.$t('delete.deleted'), { timeout: 2500 });
          this.showPdfs();
        })
        .catch(error => {
          console.error('API request error:', error); // eslint-disable-line no-console
        });
    },

    // Navigation
    goToTool(item) {
      this.$router.push({ name: 'Tool', params: { toolId: item.toolId } });
    },

    // Helpers
    formatDate(dateString) {
      const d = new Date(dateString);
      return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    },

    getStatusTranslation(statusId) {
      switch (statusId) {
        case 1: return this.$t('projectStatus.notStarted');
        case 2: return this.$t('projectStatus.active');
        case 3: return this.$t('projectStatus.onHold');
        case 4: return this.$t('projectStatus.finished');
        case 5: return this.$t('projectStatus.other');
        default: return '';
      }
    },
  },
};
</script>

<style scoped>
/* 8px + the 12px column gutter lines the header up with the 20px card margin */
.project-header {
  margin: 8px 8px 0;
}

.project-phases {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
}
.project-phases-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-right: 4px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #7b809a;
}
.project-phases-label .material-icons {
  font-size: 15px;
}
.project-phase-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 11px;
  border-radius: 20px;
  background: #eef1f6;
  color: #344767;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.2;
}

.thumbs {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.thumb {
  width: 100px;
  height: auto;
  border-radius: 6px;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.thumb:hover {
  transform: scale(1.05);
}

.nav-item {
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}

.pdf-item {
  padding: 8px 12px;
  border-radius: 6px;
  background: #f8f9fa;
}

.pdf-item:hover {
  background: #e9ecef;
}

.pdf-item a {
  color: #344767;
}

.pdf-item a:hover {
  color: #1a73e8;
}
</style>
